import { Facebook, Instagram, Linkedin, Mail, Phone, MapPin, ShieldCheck } from 'lucide-react';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ReactComponent as LogoFPC } from '../ui/logoFPCH.svg';
import api from '../utils/api';
import ContractModal from './modal/ContractModal';
import { ContractData } from '../utils/contractUtils';

// Dichiarazione TypeScript per Iubenda
declare global {
  interface Window {
    _iub?: {
      cs?: {
        api?: {
          openPreferences: () => void;
        };
      };
    };
  }
}

const Footer: React.FC = () => {
  const { t } = useTranslation();
  const [showFamilyContract, setShowFamilyContract] = useState(false);
  const [showTeacherContract, setShowTeacherContract] = useState(false);
  const [familyContractData, setFamilyContractData] = useState<ContractData | null>(null);
  const [teacherContractData, setTeacherContractData] = useState<ContractData | null>(null);
  const [contractLoading, setContractLoading] = useState(false);

  const openFamilyContractModal = async () => {
    setContractLoading(true);
    try {
      const response = await api.get('/contract/cgc/family/ita', { skipAuth: true });
      setFamilyContractData(response.data);
      setShowFamilyContract(true);
    } catch (error) {
      console.error('Error loading family contract:', error);
    } finally {
      setContractLoading(false);
    }
  };

  const openTeacherContractModal = async () => {
    setContractLoading(true);
    try {
      const response = await api.get('/contract/cgc/teacher/ita', { skipAuth: true });
      setTeacherContractData(response.data);
      setShowTeacherContract(true);
    } catch (error) {
      console.error('Error loading teacher contract:', error);
    } finally {
      setContractLoading(false);
    }
  };

  return (
    <footer className="bg-gradient-to-br from-fpcwhite via-white to-fpcpink/30 border-t-4 border-fpcred relative overflow-hidden p-2 md:p-8">
      {/* Elementi decorativi blur */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-fpcred/5 rounded-full blur-3xl -mr-20 -mt-20 pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-fpcpink/30 rounded-full blur-3xl -ml-32 -mb-32 pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="py-12 md:py-14 lg:py-20">
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-5 gap-6 md:gap-10 lg:gap-12">
            {/* Logo e descrizione */}
            <div className="col-span-2 md:col-span-1 flex flex-col items-center space-y-3 md:space-y-4">
              <LogoFPC className="h-10 md:h-20 w-auto" />
              <p className="text-gray-700 text-center lg:text-left text-sm md:text-base leading-relaxed max-w-xs">
                Formando Percorsi - Ripetizioni per tutti i livelli di istruzione
              </p>

              {/* Icone social per mobile - mostrate solo sotto lg */}
              <div className="flex gap-4 lg:hidden mt-2">
                <a
                  href="https://www.facebook.com/share/18BxfxBV3z/?mibextid=wwXIfr"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 hover:text-fpcred transition-colors duration-300">
                  <span className="sr-only">Facebook</span>
                  <Facebook className="h-6 w-6" />
                </a>
                <a
                  href="https://www.instagram.com/formandopercorsi?igsh=Zmk1eDU5c2p5Yzgw&utm_source=qr"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 hover:text-fpcred transition-colors duration-300">
                  <span className="sr-only">Instagram</span>
                  <Instagram className="h-6 w-6" />
                </a>
                <a
                  href="https://www.linkedin.com/company/formando-percorsi/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 hover:text-fpcred transition-colors duration-300 hover:scale-110 transform">
                  <span className="sr-only">Linkedin</span>
                  <Linkedin className="h-6 w-6" />
                </a>
              </div>
            </div>

            {/* Link utili */}
            <div className="flex flex-col items-center space-y-2 md:space-y-3">
              <h3 className="text-fpcred font-bold text-lg md:text-xl lg:text-2xl mb-1 md:mb-2 tracking-tight">
                {t('footer.usefulLinks')}
              </h3>
              <Link
                to="/"
                className="text-gray-600 hover:text-fpcred transition-colors duration-300 text-sm md:text-base font-medium hover:translate-x-1 inline-block">
                {t('homeTitle')}
              </Link>
              <Link
                to="/chi-siamo"
                className="text-gray-600 hover:text-fpcred transition-colors duration-300 text-sm md:text-base font-medium hover:translate-x-1 inline-block">
                {t('aboutTitle')}
              </Link>
              <Link
                to="/#prezzi"
                className="text-gray-600 hover:text-fpcred transition-colors duration-300 text-sm md:text-base font-medium hover:translate-x-1 inline-block">
                {t('pricingTitle')}
              </Link>
              <Link
                to="/lavora-con-noi"
                className="text-gray-600 hover:text-fpcred transition-colors duration-300 text-sm md:text-base font-medium hover:translate-x-1 inline-block">
                {t('workWithUsTitle')}
              </Link>
            </div>

            {/* Contatti */}
            <div className="flex flex-col items-center space-y-2 md:space-y-3">
              <h3 className="text-fpcred font-bold text-lg md:text-xl lg:text-2xl mb-1 md:mb-2 tracking-tight">
                {t('footer.contactUs')}
              </h3>
              <a
                href="mailto:info.studente@formandopercorsi.com"
                className="flex items-center text-gray-600 hover:text-fpcred transition-all duration-300 group">
                <Mail className="h-4 md:h-5 w-4 md:w-5 mr-2 md:mr-3 group-hover:scale-110 transition-transform flex-shrink-0" />
                <span className="text-sm md:text-base">info.studente@formandopercorsi.com</span>
              </a>
              <a
                href="tel:+393402817105"
                className="flex items-center text-gray-600 hover:text-fpcred transition-all duration-300 group">
                <Phone className="h-4 md:h-5 w-4 md:w-5 mr-2 md:mr-3 group-hover:scale-110 transition-transform flex-shrink-0" />
                <span className="text-sm md:text-base">+39 3402817105</span>
              </a>
              <div className="flex items-center text-gray-600 group">
                <MapPin className="h-4 md:h-5 w-4 md:w-5 mr-2 md:mr-3 flex-shrink-0" />
                <span className="text-sm md:text-base">Ferrara, Italia</span>
              </div>
            </div>

            {/* Social - Solo Desktop */}
            <div className="hidden lg:flex flex-col items-center space-y-4">
              <h3 className="text-fpcred font-bold text-xl lg:text-2xl mb-2 tracking-tight">{t('footer.followUs')}</h3>
              <div className="flex gap-4">
                <a
                  href="https://www.facebook.com/share/18BxfxBV3z/?mibextid=wwXIfr"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 hover:text-fpcred transition-colors duration-300 hover:scale-110 transform">
                  <span className="sr-only">Facebook</span>
                  <Facebook className="h-6 w-6" />
                </a>
                <a
                  href="https://www.instagram.com/formandopercorsi?igsh=Zmk1eDU5c2p5Yzgw&utm_source=qr"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 hover:text-fpcred transition-colors duration-300 hover:scale-110 transform">
                  <span className="sr-only">Instagram</span>
                  <Instagram className="h-6 w-6" />
                </a>
                <a
                  href="https://www.linkedin.com/company/formando-percorsi/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 hover:text-fpcred transition-colors duration-300 hover:scale-110 transform">
                  <span className="sr-only">Linkedin</span>
                  <Linkedin className="h-6 w-6" />
                </a>
              </div>
              <h3 className="text-fpcred font-bold text-xl lg:text-2xl mb-2 tracking-tight">Illustratore</h3>
              <a
                href="https://www.behance.net/sebastiirimesc1"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-fpcred transition-colors duration-300">
                Sebastian Irimescu
              </a>
            </div>

            {/* Contratti */}
            <div className="flex flex-col items-center space-y-2 md:space-y-3">
              <h3 className="text-fpcred font-bold text-lg md:text-xl lg:text-2xl mb-1 md:mb-2 tracking-tight">
                {t('footer.contracts')}
              </h3>
              <button
                onClick={openFamilyContractModal}
                className="text-gray-600 hover:text-fpcred transition-colors duration-300 text-sm md:text-base font-medium hover:translate-x-1 inline-block bg-transparent border-none cursor-pointer p-0">
                {t('footer.familyContract')}
              </button>
              <button
                onClick={openTeacherContractModal}
                className="text-gray-600 hover:text-fpcred transition-colors duration-300 text-sm md:text-base font-medium hover:translate-x-1 inline-block bg-transparent border-none cursor-pointer p-0">
                {t('footer.teacherContract')}
              </button>
            </div>
          </div>

          {/* Copyright */}
          <div className="border-t border-gray-300 pt-8 pb-6 mt-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <p className="text-gray-500 text-sm lg:text-base text-center md:text-left">
                FPC DIDATTICA 4.0 S.R.L. | Partita IVA: IT02141830386 | PEC: admin.fpcdidattica4.0@pec.it Copyright ©{' '}
                {new Date().getFullYear()} {t('footer.allRightsReserved')}
              </p>

              {/* Link legali */}
              <div className="flex items-center gap-6 text-sm">
                <a
                  href="https://www.iubenda.com/privacy-policy/45594522"
                  className="iubenda-white iubenda-noiframe iubenda-embed iubenda-noiframe"
                  title="Privacy Policy">
                  Privacy Policy
                </a>
                <a
                  href="https://www.iubenda.com/privacy-policy/45594522/cookie-policy"
                  className="iubenda-white iubenda-noiframe iubenda-embed iubenda-noiframe"
                  title="Cookie Policy"
                  target="_blank"
                  rel="noopener noreferrer">
                  Cookie Policy
                </a>
                <button
                  onClick={() => {
                    if (window._iub && window._iub.cs && window._iub.cs.api) {
                      window._iub.cs.api.openPreferences();
                    }
                  }}
                  className="flex items-center px-2 py-2 bg-white text-gray-900 hover:bg-gray-50 transition-all duration-300 border border-gray-200 rounded-md cursor-pointer text-sm font-medium shadow-sm hover:shadow-md"
                  title="Preferenze Privacy">
                  <ShieldCheck className="h-5 w-5 text-green-600" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contract Modals */}
      <ContractModal
        isOpen={showFamilyContract}
        onClose={() => setShowFamilyContract(false)}
        contractData={familyContractData}
        isLoading={contractLoading}
      />
      <ContractModal
        isOpen={showTeacherContract}
        onClose={() => setShowTeacherContract(false)}
        contractData={teacherContractData}
        isLoading={contractLoading}
      />
    </footer>
  );
};

export default Footer;
